import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useImmer } from "use-immer";

import { IgButton, IgPaper, IgTypography } from "@intelligems/ig-theme";
import { getIntelligemsAppScopes } from "@intelligems/ig-utils-strict";
import { Grid } from "@mui/material";
import {
  useGetOrg,
  useGetOrgId,
} from "@src/api/organizations/get-organization";
import { useIsAdmin } from "@src/api/user-auths/is-admin";
import { useIsIntelligems } from "@src/api/user-auths/is-intelligems";
import { useRefreshShopifyScopes, useUser } from "@src/api/user-auths/login";
import { BillingPanel } from "@src/components/BillingPanel/BillingPanel";
import {
  checkAnyScope,
  checkBooleanScope,
} from "@src/components/LimitAccess/check-scopes";
import { useHasRequiredShopifyScopes } from "@src/components/LimitAccess/hooks";
import { IgAlert } from "@src/components/Utils/IgAlert";
import { IgErrorBoundary } from "@src/components/Utils/IgErrorBoundary";
import { AdvancedActions } from "@src/domains/Settings/AdvancedActions/AdvancedActions";
import { AnalyticsSettings } from "@src/domains/Settings/AnalyticsSettings";
import { useViewPage } from "@src/hooks/mixpanel";
import { uninstallIntelligems } from "@src/utils/linkSheet";
import { openNewTab } from "@src/utils/openNewTab";

import Loading from "../../utils/Loading";

import { AdvancedSettings } from "./AdminSettings/AdvancedSettings";
import { IntegrationStatusSwitcher } from "./AdminSettings/IntegrationStatusSwitcher";
import { CogsPanel } from "./Cogs/CogsPanel";
import { PricingStatusSettingsSection } from "./PricingStatus/pricingStatusSettingsSection";
import { WebhookSettings } from "./WebhookSettings/WebhookSettings";
import { CurrencySettings } from "./CurrencySettings";
import { GlobalCssAndJsSettings } from "./GlobalCssAndJsSettings";
import { OrganizationInformation } from "./OrganizationInformation";
import { PersonalInformation } from "./PersonalInformation";
import { ScopeSettings } from "./ScopeSettings";
import { ScriptCopy } from "./ScriptCopy";

export const Settings = () => {
  const organization = useGetOrg();
  const organizationId = useGetOrgId();
  const user = useUser();
  const location = useLocation();
  const isAdmin = useIsAdmin();
  const isIntelligems = useIsIntelligems();
  const refreshShopifyCredentials = useRefreshShopifyScopes();
  const hasRequiredShopifyScopes = useHasRequiredShopifyScopes([
    ...getIntelligemsAppScopes(import.meta.env.VITE_STAGE),
  ]);

  useViewPage("Settings");

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.replace("#", ""));
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [location.hash]);

  if (user.isSuccess) {
    return (
      <IgErrorBoundary>
        <Grid
          container
          spacing={2}
          direction={"column"}
          // alignItems={"flex-start"}
        >
          <Grid item>
            <IgTypography variant="h1" fontWeight={"fontWeightMedium"}>
              Settings
            </IgTypography>
          </Grid>

          {hasRequiredShopifyScopes ||
          hasRequiredShopifyScopes === undefined ? null : (
            <Grid item>
              <IgAlert
                severity={"warning"}
                title={"Update Shopify Permissions"}
                longMessage={
                  <IgTypography>
                    Please reauthorize your store to access latest features.
                  </IgTypography>
                }
                buttons={[
                  <Grid
                    item
                    key={"update-permissions"}
                    sx={{ marginTop: "auto", marginBottom: "auto" }}
                  >
                    <IgButton
                      variant={"contained"}
                      color={"error"}
                      loading={refreshShopifyCredentials.isPending}
                      onClick={() => refreshShopifyCredentials.mutate({})}
                    >
                      Update Permissions
                    </IgButton>
                  </Grid>,
                ]}
              />
            </Grid>
          )}

          <Grid item container direction={"row"} columnSpacing={2}>
            <Grid item xs={6}>
              <OrganizationInformation />
            </Grid>
            <Grid item xs={6}>
              <PersonalInformation />
            </Grid>
          </Grid>

          <Grid item xs>
            <ScriptCopy organizationId={organizationId} />
          </Grid>
          <Grid item sx={{ width: "100%" }}>
            <BillingPanel managePlans={true} />
          </Grid>
          <Grid item xs>
            <WebhookSettings />
          </Grid>
          {isAdmin && organization && organization.data.org && (
            <>
              <Grid item xs>
                <IgPaper>
                  <PricingStatusSettingsSection />
                </IgPaper>
              </Grid>
              <Grid item xs>
                <IgPaper>
                  <IntegrationStatusSwitcher />
                </IgPaper>
              </Grid>
            </>
          )}
          <Grid item xs>
            <CogsPanel />
          </Grid>
          <Grid item xs>
            <AnalyticsSettings />
          </Grid>
          <Grid item xs>
            <GlobalCssAndJsSettings type={"css"} />
          </Grid>
          <Grid item xs>
            <GlobalCssAndJsSettings type={"javascript"} />
          </Grid>
          <Grid item xs>
            <CurrencySettings />
          </Grid>
          <Grid item xs>
            <AdvancedActions />
          </Grid>
          <Grid item xs>
            <IgPaper>
              <Grid item>
                <IgTypography>
                  Need to uninstall Intelligems? Learn more{" "}
                  <IgTypography
                    onClick={() => {
                      openNewTab(
                        uninstallIntelligems,
                        "Uninstall Intelligems",
                        true
                      );
                    }}
                    igStyle={"link"}
                  >
                    here.
                  </IgTypography>
                </IgTypography>
              </Grid>
            </IgPaper>
          </Grid>
          {checkAnyScope([
            checkBooleanScope(
              "edit_advanced_settings",
              organization.data.org.configurations
            ),
            isIntelligems || false,
          ]) && (
            <Grid item xs>
              <AdvancedSettings />
            </Grid>
          )}
          {isAdmin && (
            <Grid item xs>
              <IgPaper>
                <ScopeSettings />
              </IgPaper>
            </Grid>
          )}
        </Grid>
      </IgErrorBoundary>
    );
  }
  return <Loading />;
};
